import {
	buildDidYouMean,
	buildFieldSortCriterion,
	buildQuerySummary,
	buildRelevanceSortCriterion,
	buildResultList,
	buildSearchParameterManager,
	buildSort,
	DidYouMeanState,
	loadAdvancedSearchQueryActions,
	loadQueryActions,
	loadSearchAnalyticsActions,
	SearchEngine,
	SearchParameters,
	SortOrder
} from "@coveo/headless";
import { useEffect, useMemo, useState } from "react";
import deserializeUrl from "components/coveo-headless/utils/deserialize-url";
import serializeUrl from "components/coveo-headless/utils/serialize-url";
import {
	CoveoHeadlessData,
	CoveoHeadlessTemplate,
	SortByDictionary,
	standaloneSearchBoxStorageKey,
	CoveoHeadlessSortBy
} from "components/coveo-headless/types/coveo-headless-types";
import PublicSearchTotalResults from "components/search/public/PublicSearchTotalResults";
import setDefaultSort from "components/coveo-headless/utils/setDefaultSort";
import {
	registerField,
	registerSearchHub,
	launchAnalytics
} from "components/coveo-headless/utils/initAnalytics";
import PublicSiteSearchPageResults from "./PublicSiteSearchPageResults";
import { PublicSiteResultsPlaceholderPage } from "components/search/search-results-placeholders/PublicSiteResultsPlaceholderPage";
import PublicSearchPageSortBy from "./PublicSearchPageSortBy";

type PublicSiteSearchPageProps = {
	engine: SearchEngine;
	jsonData: CoveoHeadlessData;
	pageType: string;
	searchHub: string;
	templates: CoveoHeadlessTemplate[];
};

const PublicSiteSearchPage = ({
	engine,
	jsonData,
	pageType,
	searchHub,
	templates
}: PublicSiteSearchPageProps) => {
	const {
		aq,
		context,
		fields,
		filters,
		noresults,
		resultDescription,
		showMoreText,
		tabs,
		didYouMean
	} = jsonData;

	// available sort values
	const criteriaDictionary: SortByDictionary[] = [
		{
			name: "Relevancy",
			criterion: buildRelevanceSortCriterion()
		},
		{
			name: "Newest",
			criterion: buildFieldSortCriterion("publishdate", SortOrder.Descending)
		},
		{
			name: "Oldest",
			criterion: buildFieldSortCriterion("publishdate", SortOrder.Ascending)
		}
	];
	const defaultSort = setDefaultSort(filters.sortCriteria, criteriaDictionary);
	// string values to use for serialize-url function
	const sortByDictionary = [
		CoveoHeadlessSortBy.Relevancy,
		CoveoHeadlessSortBy.PublishDateAsc,
		CoveoHeadlessSortBy.PublishDateDesc
	];

	const sortCriteria = useMemo(() => buildSort(engine), [engine]);
	const querySummary = useMemo(() => buildQuerySummary(engine), [engine]);
	const resultList = useMemo(() => buildResultList(engine), [engine]);
	const coveoSuggestions = useMemo(
		() => buildDidYouMean(engine, { options: { queryCorrectionMode: "next" } }),
		[engine]
	); // "next" is coveo ML algo
	const [coveoSuggestionsState, setCoveoSuggestionState] = useState<DidYouMeanState>();
	const [isLoading, setIsLoading] = useState(true);
	const [loaderState, setLoaderState] = useState({ isLoading: true });

	useEffect(() => {
		resultList.subscribe(() => {
			setIsLoading(true);
			setLoaderState(resultList.state);
		});
	}, [resultList]);

	useEffect(() => {
		if (loaderState?.isLoading === false) {
			setIsLoading(false);
		}
	}, [loaderState]);

	useEffect(() => {
		coveoSuggestions.subscribe(() => setCoveoSuggestionState(coveoSuggestions.state));
	}, [coveoSuggestions]);

	useEffect(() => {
		const hash = window.location.hash.slice(1);
		let parameters: SearchParameters = deserializeUrl(
			aq,
			defaultSort.criteria,
			sortByDictionary,
			tabs,
			hash
		);
		if (Object.keys(parameters).length === 0) {
			parameters.aq = aq;
			parameters.sortCriteria = defaultSort.criteria;
		}

		const searchParameterManager = buildSearchParameterManager(engine, {
			initialState: {
				parameters: {
					aq: parameters.aq,
					q: parameters.q,
					f: parameters.f,
					sortCriteria: parameters.sortCriteria
				}
			}
		});
		// when user filters via UI, update url hash
		searchParameterManager.subscribe(() => {
			const url = serializeUrl(
				searchParameterManager.state.parameters,
				sortCriteria.state.sortCriteria,
				pageType
			);
			window.history.pushState(null, document.title, url);
		});

		/**
		 * when user types into url bar, deserialize new parameters, update UI
		 * update searchParameterManager with new query parameters
		 */
		const onHashChange = () => {
			const hash = window.location.hash.slice(1);
			const parameters = deserializeUrl(aq, defaultSort.criteria, sortByDictionary, tabs, hash);
			searchParameterManager.synchronize(parameters);
		};

		const { logInterfaceLoad } = loadSearchAnalyticsActions(engine);
		const { updateAdvancedSearchQueries } = loadAdvancedSearchQueryActions(engine);

		const { updateQuery } = loadQueryActions(engine);
		const { logSearchFromLink, logOmniboxFromLink } = loadSearchAnalyticsActions(engine);

		engine.dispatch(updateAdvancedSearchQueries({ aq: aq }));

		// Example redirectSearchData from typed value:
		// {"value":"etfs","analytics":{"cause":"searchFromLink","metadata":null}}
		//  Example redirectSearchData from selected coveo suggestion:
		// {"value":"tuning out the noise","analytics":{"cause":"omniboxFromLink",
		// "metadata":{"suggestionRanking":1,"partialQuery":"out","partialQueries":["out"],
		// "suggestions":["out","tuning out the noise"],
		// "querySuggestResponseId":"e24a17d2-70cb-426f-af68-3f3432f3162b"}}}
		// We allow people to hit enter without any input value which results in:
		// {"value":"","analytics":{"cause":"","metadata":null}}
		const redirectSearchData = localStorage.getItem(standaloneSearchBoxStorageKey);

		if (redirectSearchData) {
			localStorage.removeItem(standaloneSearchBoxStorageKey);

			const { value, analytics } = JSON.parse(redirectSearchData);
			const { cause, metadata } = analytics;

			if (value) {
				const event =
					cause === "searchFromLink" ? logSearchFromLink() : logOmniboxFromLink(metadata);
				engine.dispatch(updateQuery({ q: value }));
				engine.executeFirstSearch(event);
			} else {
				engine.executeFirstSearch(logInterfaceLoad());
			}
		} else {
			engine.executeFirstSearch(logInterfaceLoad());
		}

		window.addEventListener("hashchange", onHashChange);

		return () => {
			window.removeEventListener("hashchange", onHashChange);
		};
		// adding all dependencies react recommends cause coveo to go into an infinite loop
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [aq, engine, filters.pipeline, tabs]);

	launchAnalytics(engine, context);
	registerField(engine, fields);
	registerSearchHub(engine, searchHub);

	const coveoDidYouMean = {
		applyCorrection: coveoSuggestions.applyCorrection,
		coveoSuggestionsState,
		...didYouMean
	};

	return isLoading || !coveoSuggestions ? (
		<PublicSiteResultsPlaceholderPage numberOfCards={Number(filters.numberOfResults)} />
	) : (
		<div className="public-search-page">
			{/** Only show results page if there is a query parameter and coveo returns results for that query */}
			{engine.state.query && engine.state.query.q !== "" && resultList.state.hasResults ? (
				<>
					<section className="public-search-page-header-container">
						<div className="public-search-page-header ddx-container">
							<div className="public-search-page-header-content">
								<PublicSearchTotalResults
									controller={querySummary}
									showInFilters={false}
									pageType={pageType}
									resultDescription={resultDescription}
									coveoDidYouMean={coveoDidYouMean}
								/>
								<PublicSearchPageSortBy
									controller={sortCriteria}
									criteriaDictionary={criteriaDictionary}
								/>
							</div>
						</div>
					</section>
					<PublicSiteSearchPageResults
						controller={resultList}
						headlessEngine={engine}
						showMoreText={showMoreText}
						templates={templates}
					/>
				</>
			) : (
				<div className="public-search-no-results ddx-container">
					<div className="t-heading-lg">
						{noresults ? `${noresults.text} ` : "We are sorry but there are no results for "}
						<span className="query-text">{querySummary.state.query}</span>
					</div>
					<div className="t-body-md">
						{noresults && noresults.subtext ? noresults.subtext : "Please try another search term."}
					</div>
				</div>
			)}
		</div>
	);
};

export default PublicSiteSearchPage;
